// @ts-check
// Consultas internas (/service/consultations e /service/consultations/:ticketId; contrato 10 de atendimento, seções 9,
// 17.5 e 24.3): as consultas para o membro ("Abertas" e "Respondidas"), o detalhe com o aluno (nome, série, turma e
// foto), a pergunta, quem perguntou e o campo "Responder". É a tela do professor: nada da família, do telefone nem da
// conversa aparece aqui, porque o servidor manda só a projeção do aluno.
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { answerConsultation, listConsultations } from '../../data/service-desk.js';
import { formatDateTime } from '../../lib/format.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Avatar } from '../../ui/avatar.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { TabList } from '../../ui/tab-list.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { useIsMobile } from '../../ui/hooks.js';
import { SlaChip, TicketValueTag, serviceErrorMessage } from './service-display.js';
import { t } from '../../i18n/index.js';

/** @typedef {{ ticket: import('../../data/service-desk.js').ServiceTicket, student: { name: string | null, gradeLevel: string | null, classOffering: string | null, avatarUrl: string | null },
 *   askedBy: { workspaceMemberId: string, name: string } | null, askedAt: string, question?: string, answer?: { body: string, answeredAt: string } | null }} ConsultationItem */

/** @param {{ item: ConsultationItem, onAnswered: () => void, onBack: (() => void) | null }} props */
function ConsultationDetail({ item, onAnswered, onBack }) {
  const [answer, setAnswer] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const isOpen = !['RESOLVED', 'CLOSED'].includes(item.ticket.status);
  const send = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await answerConsultation(item.ticket.id, answer.trim());
      showSuccessSnackbar(t('serviceDesk.consultations.answered'));
      setAnswer('');
      onAnswered();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<article class="service-consultation-detail" data-testid="service-consultation-detail">
    ${onBack ? html`<${LightIconButton} icon="IconChevronLeft" size="medium" ariaLabel=${t('serviceDesk.pane.backToList')} onClick=${onBack} />` : null}
    <header class="service-consultation-student">
      <${Avatar} placeholder=${item.student.name ?? '?'} seed=${item.ticket.id} size="xl" type="rounded" avatarUrl=${item.student.avatarUrl} />
      <div>
        <h2>${item.student.name ?? t('serviceDesk.consultations.noStudent')}</h2>
        <span class="service-consultation-meta">${[item.student.gradeLevel, item.student.classOffering].filter(Boolean).join(' · ')}</span>
      </div>
      <span class="service-pane-title-spacer"></span>
      <${TicketValueTag} field="status" value=${item.ticket.status} />
      <${SlaChip} ticket=${item.ticket} compact=${true} />
    </header>
    <section class="service-consultation-question">
      <span class="service-consultation-meta">
        <${Icon} name="IconMessageQuestion" size=${14} />
        ${t('serviceDesk.consultations.askedBy', { name: item.askedBy?.name ?? '', date: formatDateTime(item.askedAt) })}
        <span class="service-protocol">${item.ticket.protocol}</span>
      </span>
      <p>${item.question ?? item.ticket.summary ?? ''}</p>
    </section>
    ${item.answer
      ? html`<section class="service-consultation-answer"><span class="service-consultation-meta"><${Icon} name="IconCheck" size=${14} />
          ${t('serviceDesk.consultations.answeredAt', { date: formatDateTime(item.answer.answeredAt) })}</span><p>${item.answer.body}</p></section>`
      : null}
    ${isOpen
      ? html`<section class="service-consultation-reply">
          <label class="form-field">
            <span class="form-field-label">${t('serviceDesk.consultations.reply')}</span>
            <textarea class="text-input service-textarea" rows="5" maxLength=${4000} value=${answer} placeholder=${t('serviceDesk.consultations.replyPlaceholder')}
              data-testid="service-consultation-answer" onInput=${(/** @type {Event} */ event) => setAnswer(/** @type {HTMLTextAreaElement} */ (event.currentTarget).value)}></textarea>
            <span class="input-hint">${t('serviceDesk.consultations.replyHint')}</span>
          </label>
          ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
          <div class="service-inline-actions"><${Button} title=${t('serviceDesk.consultations.send')} icon="IconSend" accent="blue" size="small" disabled=${!answer.trim()} isLoading=${isSaving} onClick=${send} /></div>
        </section>`
      : null}
    <p class="service-privacy-note"><${Icon} name="IconLock" size=${14} />${t('serviceDesk.consultations.privacy')}</p>
  </article>`;
}

/** @param {{ ticketId?: string | null }} props */
export function ConsultationsPage({ ticketId = null }) {
  const tab = searchParamsState.value.get('status') === 'answered' ? 'answered' : 'open';
  const [items, setItems] = useState(/** @type {ConsultationItem[] | null} */ (null));
  const [openCount, setOpenCount] = useState(/** @type {number | null} */ (null));
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'error'} */ ('loading'));
  const [selectedItem, setSelectedItem] = useState(/** @type {ConsultationItem | null} */ (null));
  const isMobile = useIsMobile();

  const load = async () => {
    try {
      const [list, open] = await Promise.all([listConsultations(tab), tab === 'open' ? null : listConsultations('open')]);
      setItems(list);
      setOpenCount((tab === 'open' ? list : open ?? []).length);
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  };
  useEffect(() => {
    document.title = t('serviceDesk.screens.consultations');
  }, []);
  useEffect(() => {
    setStatus('loading');
    setItems(null);
    load();
  }, [tab]);
  // a consulta aberta pela URL pode estar na outra aba (respondida): procura nas duas
  useEffect(() => {
    if (!ticketId) {
      setSelectedItem(null);
      return;
    }
    const found = items?.find((item) => item.ticket.id === ticketId) ?? null;
    if (found) {
      setSelectedItem(found);
      return;
    }
    listConsultations(tab === 'open' ? 'answered' : 'open')
      .then((other) => setSelectedItem(other.find((item) => item.ticket.id === ticketId) ?? null))
      .catch(() => setSelectedItem(null));
  }, [ticketId, items]);

  const showList = !isMobile || !ticketId;
  const showDetail = !isMobile || Boolean(ticketId);
  const search = tab === 'answered' ? '?status=answered' : '';
  return html`<${PageCard} className="service-screen service-consultations-card"
    header=${html`<${PageHeader} icon="IconMessageQuestion" title=${t('serviceDesk.screens.consultations')} />`}>
    <div class="service-consultations" data-testid="service-consultations">
      ${showList
        ? html`<section class="service-consultations-list">
            <${TabList} tabs=${[
              { id: 'open', title: t('serviceDesk.consultations.tabs.open'), icon: 'IconMessageQuestion', pill: openCount ?? undefined },
              { id: 'answered', title: t('serviceDesk.consultations.tabs.answered'), icon: 'IconCircleCheck' },
            ]} activeTabId=${tab} onChange=${(next) => updateSearchParams({ status: next === 'answered' ? 'answered' : null })} />
            ${status === 'loading'
              ? html`<div class="service-inbox-skeleton">${[0, 1, 2].map((index) => html`<${Skeleton} key=${index} height=${56} />`)}</div>`
              : status === 'error'
                ? html`<div class="service-empty"><${EmptyState} icon="IconAlertTriangle" title=${t('serviceDesk.list.errorTitle')}
                    action=${html`<${Button} title=${t('serviceDesk.list.retry')} size="small" variant="secondary" onClick=${load} />`} /></div>`
                : (items ?? []).length === 0
                  ? html`<div class="service-empty" data-testid="service-consultations-empty"><${EmptyState} icon="IconMessageQuestion" title=${t(`serviceDesk.consultations.empty.${tab}`)}
                      subtitle=${t('serviceDesk.consultations.emptyHint')} /></div>`
                  : html`<ul class="service-inbox-ul">
                      ${(items ?? []).map((item) => html`<li key=${item.ticket.id}>
                        <button type="button" class="service-inbox-row" data-selected=${item.ticket.id === ticketId ? 'true' : undefined}
                          onClick=${() => navigate(`/service/consultations/${item.ticket.id}${search}`)}>
                          <span class="service-inbox-row-avatar"><${Avatar} placeholder=${item.student.name ?? '?'} seed=${item.ticket.id} size="lg" type="rounded" avatarUrl=${item.student.avatarUrl} /></span>
                          <span class="service-inbox-row-texts">
                            <span class="service-inbox-row-top"><span class="service-inbox-row-name">${item.student.name ?? ''}</span>
                              <span class="service-inbox-row-time">${formatDateTime(item.askedAt)}</span></span>
                            <span class="service-inbox-row-preview">${item.question ?? item.ticket.summary ?? ''}</span>
                            <span class="service-inbox-row-meta"><span class="service-inbox-row-team">${[item.student.gradeLevel, item.student.classOffering].filter(Boolean).join(' · ')}</span>
                              <span class="service-inbox-row-spacer"></span><span class="service-who-detail">${t('serviceDesk.consultations.fromName', { name: item.askedBy?.name ?? '' })}</span></span>
                          </span>
                        </button>
                      </li>`)}
                    </ul>`}
          </section>`
        : null}
      ${showDetail
        ? selectedItem
          ? html`<${ConsultationDetail} key=${selectedItem.ticket.id} item=${selectedItem} onBack=${isMobile ? () => navigate(`/service/consultations${search}`) : null}
              onAnswered=${() => {
                load();
                navigate(`/service/consultations/${selectedItem.ticket.id}?status=answered`);
              }} />`
          : html`<div class="service-pane service-pane-empty"><${EmptyState} icon="IconMessageQuestion" title=${t('serviceDesk.consultations.noSelectionTitle')}
              subtitle=${t('serviceDesk.consultations.noSelection')} /></div>`
        : null}
    </div>
  <//>`;
}
